/**
 * SRFooter Component
 * ==================
 * Site footer with navigation columns
 * ALL content from Blueprint - NO hardcoded text or routes
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useBlueprint } from '../../context/BlueprintContext';
import { Instagram, Globe, Linkedin, ArrowRight } from 'lucide-react';

const SRFooter = () => {
  const { footer, navbar, lang, getRoute } = useBlueprint();
  const [email, setEmail] = useState('');
  
  // Safe text extraction helper
  const safeText = (value, fallback = '') => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      if (value.text) return safeText(value.text, fallback);
      if (value.label) return safeText(value.label, fallback);
      return fallback;
    }
    return fallback;
  };
  
  const logo = navbar?.logo || {};
  let columns = footer?.columns || [];
  const newsletter = footer?.newsletter || {};
  
  // Default footer columns if CMS is empty
  if (columns.length === 0) {
    columns = [
      {
        title: 'Esplora',
        links: [
          { text: 'Materiali', route: '/materials' },
          { text: 'Ricerca Visiva', route: '/visual-search' }
        ]
      },
      {
        title: 'Risorse',
        links: [
          { text: 'Progetti', route: '/projects' },
          { text: 'DNA Compare', route: '/dna-compare' }
        ]
      },
      {
        title: 'Azienda',
        links: [
          { text: 'Chi Siamo', route: '/about' },
          { text: 'Contatti', route: '/contact' }
        ]
      }
    ];
  }
  
  // Safe logo values
  const logoMark = safeText(logo?.mark, 'SR');
  const logoName = safeText(logo?.name, 'SLABREALITY');
  const footerTagline = safeText(footer?.tagline, '');
  const footerDescription = safeText(footer?.description, '');
  const footerCopyright = safeText(footer?.copyright, '© 2024');
  const newsletterTitle = safeText(newsletter?.title, 'Newsletter');
  const newsletterDescription = safeText(newsletter?.description, '');
  const newsletterPlaceholder = safeText(newsletter?.placeholder, 'Email');
  
  // Resolve link using config routes
  const resolveLink = (link) => {
    if (link?.startsWith('/')) {
      return link;
    }
    return getRoute(link, '/');
  };
  
  // Get home route for logo
  const homeRoute = getRoute('home', '/');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Newsletter signup:', email);
    setEmail('');
  };
  
  return (
    <footer className="sr-footer py-20 px-8">
      <div className="sr-container">
        {/* Top */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10 pb-16 border-b border-[#222220]">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 border-[1.5px] border-[var(--gold)] flex items-center justify-center font-[var(--ff-h)] text-[15px] font-bold text-[var(--gold)]">
                {logoMark}
              </div>
              <div>
                <div className="text-xs font-extrabold tracking-[3.5px] text-white leading-tight">
                  {logoName}
                </div>
                <div className="text-[7.5px] tracking-[1.2px] text-[#666]">
                  {footerTagline}
                </div>
              </div>
            </div>
            
            <p className="text-[13px] text-[#999] leading-[1.8] mb-7 max-w-[220px]">
              {footerDescription}
            </p>
            
            {/* Social */}
            <div className="flex gap-2.5">
              <a href="#" className="w-[38px] h-[38px] rounded-full border border-[#333] flex items-center justify-center text-[#AAA] hover:border-[var(--gold)] hover:bg-[rgba(169,136,101,0.1)] transition-all">
                <Instagram size={16} />
              </a>
              <a href="#" className="w-[38px] h-[38px] rounded-full border border-[#333] flex items-center justify-center text-[#AAA] hover:border-[var(--gold)] hover:bg-[rgba(169,136,101,0.1)] transition-all">
                <Globe size={16} />
              </a>
              <a href="#" className="w-[38px] h-[38px] rounded-full border border-[#333] flex items-center justify-center text-[#AAA] hover:border-[var(--gold)] hover:bg-[rgba(169,136,101,0.1)] transition-all">
                <Linkedin size={16} />
              </a>
            </div>
          </div>
          
          {/* Nav Columns */}
          {columns.map((column, i) => (
            <div key={i}>
              <h4 className="text-[10px] font-bold tracking-[2.5px] uppercase text-[#CCC] mb-5">
                {safeText(column.title, `Column ${i}`)}
              </h4>
              <ul className="flex flex-col gap-3">
                {(column.links || []).map((link, j) => (
                  <li key={j}>
                    <Link to={resolveLink(link?.url || link?.route)} className="text-[13.5px] text-[#999] hover:text-[var(--gold)] transition-colors">
                      {safeText(link?.text || link?.label, `Link ${j}`)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          
          {/* Newsletter */}
          <div className="lg:col-span-1">
            <h4 className="text-[10px] font-bold tracking-[2.5px] uppercase text-[#CCC] mb-3.5">
              {newsletterTitle}
            </h4>
            <p className="text-[13px] text-[#888] leading-[1.75] mb-5">
              {newsletterDescription}
            </p>
            <form onSubmit={handleSubmit} className="flex">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={newsletterPlaceholder}
                className="flex-1 px-4 py-3 bg-[#141412] border border-[#2E2E2C] border-r-0 rounded-l text-white text-[13px] font-[var(--ff-b)] outline-none focus:border-[var(--gold)] transition-colors"
              />
              <button
                type="submit"
                className="px-4 py-3 bg-[var(--gold)] border-none rounded-r cursor-pointer hover:bg-[var(--gold-lt)] transition-colors"
              >
                <ArrowRight size={16} className="text-[#0A0A09]" />
              </button>
            </form>
          </div>
        </div>
        
        {/* Bottom */}
        <div className="flex items-center justify-between pt-7 flex-wrap gap-3">
          <p className="text-[12.5px] text-[#666]">
            {footerCopyright}
          </p>
          <button className="flex items-center gap-1.5 text-[12.5px] text-[#666] cursor-pointer">
            <Globe size={13} />
            {lang === 'it' ? 'Italiano' : lang === 'en' ? 'English' : 'Español'}
          </button>
        </div>
      </div>
    </footer>
  );
};

export default SRFooter;
